/**
 * BR-CP-HOME-004 — the environment badge (frontend-ui-system.md § Environment badge, binding): DEV in green, STAGING
 * in amber, and nothing at all in Production. One component for both places it appears — the top of the portal
 * navigation, and under the logo on the public pages — so the two cannot drift.
 */

/** Runtime config label (DEV / STAGING / PROD) as the badge shows it; null in Production or when unset. */
export function environmentBadge(envLabel: string | undefined | null): 'DEV' | 'STAGING' | null {
  const label = (envLabel ?? '').trim().toUpperCase();
  if (label === 'DEV') return 'DEV';
  if (label === 'STAGING') return 'STAGING';
  return null;
}

/**
 * Renders the badge from `/config.js`'s `envLabel`, or nothing in Production.
 * `rail`: inside the portal navigation, where the md–lg icon rail collapses it to a coloured dot and the label
 * returns when the rail widens (hover or keyboard focus) and at lg.
 */
export function EnvironmentBadge({ rail = false, className = '' }: { rail?: boolean; className?: string }) {
  const env = environmentBadge(window.__BABOMRS_CONFIG__?.envLabel);
  if (!env) return null;
  const dev = env === 'DEV';
  return (
    <span
      data-testid="env-badge"
      title="Dev and Staging only — hidden in Production"
      className={`self-start inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide whitespace-nowrap ${
        dev ? 'bg-green-100 text-green-700 border-green-300' : 'bg-amber-100 text-amber-700 border-amber-300'
      } ${className}`}
    >
      {/* The prototype's pill has no dot; the collapsed icon rail (md–lg) shows the pill as a dot (#27999). */}
      {rail && (
        <span
          aria-hidden="true"
          className={`hidden md:inline-block lg:hidden md:group-hover/nav:hidden md:group-focus-within/nav:hidden h-2 w-2 rounded-full shrink-0 ${dev ? 'bg-green-600' : 'bg-amber-500'}`}
        />
      )}
      <span className={rail ? 'md:sr-only md:group-hover/nav:not-sr-only md:group-focus-within/nav:not-sr-only lg:not-sr-only' : ''}>
        {env}
      </span>
    </span>
  );
}
